import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import apiClient from '../../services/apiClient';
import LocationPicker from '../../components/logistica/LocationPicker.jsx';
import {
  Page, Header, Title, Controls, SearchInput, BackBtn, AddBtn,
  TableWrap, Table, Th, Td, ActionsCell, ActionBtn,
  Overlay, ModalBox, ModalHead, ModalTitle, CloseBtn, ModalBody, ModalFoot,
  FormGrp, Lbl, Inp, Row2, ErrMsg, BtnPrimary, BtnSecondary, BtnDanger,
} from '../../components/configuracion/logisticaLayoutStyles';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';
import { useToast } from '../../components/common/Toast';

const Textarea = styled.textarea`
  width: 100%; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.95rem;
  min-height: 80px; box-sizing: border-box; resize: vertical;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
`;

const EMPTY = {
  nombre: '', cuit: '', provincia: '', localidad: '', direccion: '', codigo_postal: '',
  latitud: '', longitud: '', contacto_nombre: '', contacto_telefono: '', contacto_correo: '',
  observaciones: '',
};

const desdeItem = (item) => ({
  nombre: item.nombre || '',
  cuit: item.cuit || '',
  provincia: item.provincia || '',
  localidad: item.localidad || '',
  direccion: item.direccion || '',
  codigo_postal: item.codigo_postal || '',
  latitud: item.latitud != null ? String(item.latitud) : '',
  longitud: item.longitud != null ? String(item.longitud) : '',
  contacto_nombre: item.contacto_nombre || '',
  contacto_telefono: item.contacto_telefono || '',
  contacto_correo: item.contacto_correo || '',
  observaciones: item.observaciones || '',
});

const coordenada = (valor) => {
  const v = parseFloat(valor);
  return Number.isNaN(v) ? null : parseFloat(v.toFixed(8));
};

/**
 * Catálogo de proveedores (talleres y concesionarios externos que realizan servicios
 * de mantenimiento). Con `modalOnly` se usa como alta rápida (botón +) desde la planilla.
 */
export default function ConfiguracionProveedores({ modalOnly = false, onRecordSaved = null, onRequestClose = null }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [mapLocation, setMapLocation] = useState(null);
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('proveedores/', { params: { page_size: 10000 } });
      setItems(Array.isArray(r.data) ? r.data : (r.data.results || []));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  // En modo alta rápida no hace falta el listado: sólo el formulario.
  useEffect(() => { if (!modalOnly) cargar(); }, [cargar, modalOnly]);

  const abrir = (item = null) => {
    setEditando(item);
    setForm(item ? desdeItem(item) : EMPTY);
    setMapLocation(item?.latitud != null && item?.longitud != null ? {
      address: item.direccion || '',
      lat: parseFloat(item.latitud), lng: parseFloat(item.longitud),
      direccion: item.direccion || '', localidad: item.localidad || '', provincia: item.provincia || '',
    } : null);
    setErrors({});
    setModal(true);
  };

  useEffect(() => { if (modalOnly) abrir(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return items;
    const q = busqueda.toLowerCase();
    return items.filter(i =>
      i.nombre?.toLowerCase().includes(q) ||
      i.cuit?.toLowerCase().includes(q) ||
      i.localidad?.toLowerCase().includes(q) ||
      i.contacto_nombre?.toLowerCase().includes(q)
    );
  }, [items, busqueda]);

  const handleMapLocation = (data) => {
    setMapLocation(data);
    if (!data) {
      setForm(f => ({ ...f, latitud: '', longitud: '' }));
      return;
    }
    setForm(f => ({
      ...f,
      latitud: data.lat != null && !Number.isNaN(data.lat) ? String(data.lat) : '',
      longitud: data.lng != null && !Number.isNaN(data.lng) ? String(data.lng) : '',
      direccion: data.direccion || data.address || f.direccion,
      localidad: data.localidad || f.localidad,
      provincia: data.provincia || f.provincia,
    }));
  };

  const cerrar = () => { setModal(false); setEditando(null); onRequestClose?.(); };
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };

  const guardar = async () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = 'El nombre es obligatorio';
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        cuit: form.cuit,
        provincia: form.provincia,
        localidad: form.localidad,
        direccion: form.direccion,
        codigo_postal: form.codigo_postal,
        latitud: coordenada(form.latitud),
        longitud: coordenada(form.longitud),
        contacto_nombre: form.contacto_nombre,
        contacto_telefono: form.contacto_telefono,
        contacto_correo: form.contacto_correo,
        observaciones: form.observaciones,
      };
      const resp = editando
        ? await apiClient.patch(`proveedores/${editando.id}/`, payload)
        : await apiClient.post('proveedores/', payload);
      if (modalOnly && onRecordSaved) {
        onRecordSaved(resp.data);
        onRequestClose?.();
      } else {
        cerrar(); cargar();
      }
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') setErrors(detail);
    } finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    try {
      await apiClient.delete(`proveedores/${id}/`);
      setConfirmId(null);
      cargar();
    } catch (e) {
      setConfirmId(null);
      toast.error(e.response?.data?.detail || 'No se pudo eliminar el proveedor. Si tiene mantenimientos registrados, no se puede borrar.');
    }
  };

  const formulario = (
    <>
      <FormGrp>
        <Lbl>Nombre *</Lbl>
        <Inp autoFocus value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Ej: Concesionario oficial Toyota" />
        {errors.nombre && <ErrMsg>{Array.isArray(errors.nombre) ? errors.nombre[0] : errors.nombre}</ErrMsg>}
      </FormGrp>
      <FormGrp>
        <Lbl>CUIT</Lbl>
        <Inp value={form.cuit} onChange={e => set('cuit', e.target.value)} placeholder="XX-XXXXXXXX-X" />
      </FormGrp>
      <FormGrp>
        <Lbl>Ubicación</Lbl>
        <LocationPicker
          key={editando?.id ?? 'nuevo'}
          value={mapLocation}
          onChange={handleMapLocation}
          placeholder="Buscar el taller en el mapa..."
        />
      </FormGrp>
      <Row2>
        <FormGrp>
          <Lbl>Provincia</Lbl>
          <Inp value={form.provincia} onChange={e => set('provincia', e.target.value)} placeholder="Provincia" />
        </FormGrp>
        <FormGrp>
          <Lbl>Localidad</Lbl>
          <Inp value={form.localidad} onChange={e => set('localidad', e.target.value)} placeholder="Localidad" />
        </FormGrp>
      </Row2>
      <Row2>
        <FormGrp>
          <Lbl>Dirección</Lbl>
          <Inp value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Calle y número" />
        </FormGrp>
        <FormGrp>
          <Lbl>Código postal</Lbl>
          <Inp value={form.codigo_postal} onChange={e => set('codigo_postal', e.target.value)} placeholder="CP" />
        </FormGrp>
      </Row2>
      <Row2>
        <FormGrp>
          <Lbl>Contacto</Lbl>
          <Inp value={form.contacto_nombre} onChange={e => set('contacto_nombre', e.target.value)} placeholder="Nombre del contacto" />
        </FormGrp>
        <FormGrp>
          <Lbl>Teléfono</Lbl>
          <Inp value={form.contacto_telefono} onChange={e => set('contacto_telefono', e.target.value)} placeholder="Teléfono" />
        </FormGrp>
      </Row2>
      <FormGrp>
        <Lbl>Correo electrónico</Lbl>
        <Inp type="email" value={form.contacto_correo} onChange={e => set('contacto_correo', e.target.value)} placeholder="correo@ejemplo.com" />
        {errors.contacto_correo && <ErrMsg>{Array.isArray(errors.contacto_correo) ? errors.contacto_correo[0] : errors.contacto_correo}</ErrMsg>}
      </FormGrp>
      <FormGrp>
        <Lbl>Observaciones</Lbl>
        <Textarea value={form.observaciones} onChange={e => set('observaciones', e.target.value)} placeholder="Notas adicionales..." />
      </FormGrp>
    </>
  );

  const modalFormulario = modal ? (
    <Overlay onClick={buildOnBackdropClick(cerrar)} onMouseDown={onBackdropMouseDown}>
      <ModalBox $maxWidth="560px" onClick={e => e.stopPropagation()}>
        <ModalHead>
          <ModalTitle>{editando ? 'Editar proveedor' : 'Nuevo proveedor'}</ModalTitle>
          <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
        </ModalHead>
        <ModalBody>{formulario}</ModalBody>
        <ModalFoot>
          <BtnSecondary onClick={cerrar}>Cancelar</BtnSecondary>
          <BtnPrimary onClick={guardar} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</BtnPrimary>
        </ModalFoot>
      </ModalBox>
    </Overlay>
  ) : null;

  if (modalOnly) return modalFormulario;

  return (
    <Page>
      <Header>
        <Title><i className="fas fa-store" /> Proveedores</Title>
        <HelpToggleBtn type="button" $active={helpOpen} onClick={() => setHelpOpen(v => !v)}>
          <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
          Ayuda
        </HelpToggleBtn>
      </Header>

      {helpOpen && (
        <div style={{ marginBottom: '1rem' }}>
          <HelpPanel
            tips={[
              'Cargá acá los talleres y concesionarios oficiales donde mandás los equipos a hacer mantenimientos.',
              'También podés crear un proveedor nuevo en el momento, desde la planilla del mantenimiento, con el botón +.',
              'Un proveedor con mantenimientos registrados no se puede eliminar.',
            ]}
            nextSteps={[
              'En la planilla de un mantenimiento, activá "Realizado en terceros" y elegí el proveedor.',
            ]}
            inlineOpen
          />
        </div>
      )}
      <Controls>
        <SearchInput placeholder="Buscar proveedor..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <BackBtn onClick={() => navigate('/configuracion?seccion=mant')}>
            <i className="fas fa-arrow-left" /> Volver
          </BackBtn>
          <AddBtn onClick={() => abrir()}><i className="fas fa-plus" /> Agregar</AddBtn>
        </div>
      </Controls>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Nombre</Th>
              <Th>CUIT</Th>
              <Th>Localidad</Th>
              <Th>Contacto</Th>
              <Th>Teléfono</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><Td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Cargando...</Td></tr>}
            {!loading && filtrados.length === 0 && <tr><Td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Sin registros</Td></tr>}
            {filtrados.map(item => (
              <tr key={item.id}>
                <Td>{item.nombre}</Td>
                <Td>{item.cuit || '-'}</Td>
                <Td>{[item.localidad, item.provincia].filter(Boolean).join(', ') || '-'}</Td>
                <Td>{item.contacto_nombre || '-'}</Td>
                <Td>{item.contacto_telefono || '-'}</Td>
                <ActionsCell>
                  <ActionBtn onClick={() => abrir(item)}>Editar</ActionBtn>
                  <ActionBtn $danger onClick={() => setConfirmId(item.id)}>Eliminar</ActionBtn>
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {modalFormulario}

      {confirmId && (
        <Overlay onClick={buildOnBackdropClick(() => setConfirmId(null))} onMouseDown={onBackdropMouseDown}>
          <ModalBox $maxWidth="380px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>Confirmar eliminación</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => setConfirmId(null)}>×</CloseBtn>
            </ModalHead>
            <ModalBody><p>¿Eliminar este proveedor? Esta acción no se puede deshacer.</p></ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setConfirmId(null)}>Cancelar</BtnSecondary>
              <BtnDanger onClick={() => eliminar(confirmId)}>Eliminar</BtnDanger>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}
    </Page>
  );
}
